import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { FaArrowRight } from 'react-icons/fa';
import './AdminHeroEdit.css';

const HERO_ID = 1;

const AdminHeroEdit = () => {
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const emptyForm = useMemo(
    () => ({
      badge_text: '',
      title_prefix: '',
      title_highlight: '',
      subtitle_text: '',
      primary_cta_text: '',
      explore_text: '',
      trust_rating_number: '',
      trust_rating_text: '',
      stat1_number: '',
      stat1_label: '',
      stat2_number: '',
      stat2_label: '',
    }),
    []
  );

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/admin/hero`, { headers: getAuthHeaders() })
      .then((res) => {
        const h = res.data?.hero;
        if (!h) return setForm(emptyForm);
        setForm({
          badge_text: h.badge_text ?? '',
          title_prefix: h.title_prefix ?? '',
          title_highlight: h.title_highlight ?? '',
          subtitle_text: h.subtitle_text ?? '',
          primary_cta_text: h.primary_cta_text ?? '',
          explore_text: h.explore_text ?? '',
          trust_rating_number: h.trust_rating_number ?? '',
          trust_rating_text: h.trust_rating_text ?? '',
          stat1_number: h.stat1_number ?? '',
          stat1_label: h.stat1_label ?? '',
          stat2_number: h.stat2_number ?? '',
          stat2_label: h.stat2_label ?? '',
        });
      })
      .catch((err) => {
        console.error('Failed to load hero:', err);
        setForm(emptyForm);
      })
      .finally(() => setLoading(false));
  }, [emptyForm]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title_prefix.trim()) return alert('عنوان الهيرو (prefix) مطلوب');
    if (!form.title_highlight.trim()) return alert('عنوان الهيرو (highlight) مطلوب');
    if (!form.subtitle_text.trim()) return alert('وصف الهيرو مطلوب');
    if (!form.primary_cta_text.trim()) return alert('نص زر الحجز مطلوب');
    if (!form.explore_text.trim()) return alert('نص زر "من أنا؟" مطلوب');

    setSaving(true);
    try {
      const payload = {
        id: HERO_ID,
        ...form,
      };

      await axios.put(`${API_BASE}/api/admin/hero`, payload, {
        headers: getAuthHeaders(),
      });

      alert('تم حفظ نص الهيرو بنجاح');
      navigate('/admin');
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ نص الهيرو');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-hero-edit" dir="rtl">
      <div className="admin-hero-edit-header">
        <Link to="/admin" className="back-link">
          <FaArrowRight /> العودة للصفحة الرئيسية
        </Link>
        <h1 className="admin-page-title">تعديل نص الهيرو</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-hero-form">
        <div className="form-row">
          <label>نص البادج الدائري</label>
          <input
            type="text"
            value={form.badge_text}
            onChange={(e) => handleChange('badge_text', e.target.value)}
            placeholder="مثال: استشارات بحثية • تميز أكاديمي"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>عنوان الهيرو (prefix)</label>
          <input
            type="text"
            value={form.title_prefix}
            onChange={(e) => handleChange('title_prefix', e.target.value)}
            placeholder="لخدمات البحث العلمي"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>عنوان الهيرو (highlight)</label>
          <input
            type="text"
            value={form.title_highlight}
            onChange={(e) => handleChange('title_highlight', e.target.value)}
            placeholder="والتوجيه الأكاديمي"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>وصف الهيرو</label>
          <textarea
            value={form.subtitle_text}
            onChange={(e) => handleChange('subtitle_text', e.target.value)}
            placeholder="اكتبي نص الوصف..."
            rows={5}
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>نص زر الحجز الأساسي</label>
          <input
            type="text"
            value={form.primary_cta_text}
            onChange={(e) => handleChange('primary_cta_text', e.target.value)}
            placeholder="احجزي جلستك الآن"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>نص زر من أنا؟</label>
          <input
            type="text"
            value={form.explore_text}
            onChange={(e) => handleChange('explore_text', e.target.value)}
            placeholder="من أنا؟"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الأول (trust): رقم/نسبة</label>
          <input
            type="text"
            value={form.trust_rating_number}
            onChange={(e) =>
              handleChange('trust_rating_number', e.target.value)
            }
            placeholder="مثال: 4.8 أو 1:1"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الأول (trust): نص</label>
          <input
            type="text"
            value={form.trust_rating_text}
            onChange={(e) => handleChange('trust_rating_text', e.target.value)}
            placeholder="مثال: تقييم المتدربين أو استشارات"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الثاني (stat1): رقم</label>
          <input
            type="text"
            value={form.stat1_number}
            onChange={(e) => handleChange('stat1_number', e.target.value)}
            placeholder="مثال: +250"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الثاني (stat1): نص</label>
          <input
            type="text"
            value={form.stat1_label}
            onChange={(e) => handleChange('stat1_label', e.target.value)}
            placeholder="مثال: متدرب سعيد"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الثالث (stat2): رقم</label>
          <input
            type="text"
            value={form.stat2_number}
            onChange={(e) => handleChange('stat2_number', e.target.value)}
            placeholder="مثال: +120"
            className="form-input"
          />
        </div>

        <div className="form-row">
          <label>العنصر الثالث (stat2): نص</label>
          <input
            type="text"
            value={form.stat2_label}
            onChange={(e) => handleChange('stat2_label', e.target.value)}
            placeholder="مثال: برنامج تدريبي"
            className="form-input"
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => navigate('/admin')}
            disabled={saving}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminHeroEdit;

